// externals
import type { StateTree } from '@makemydeal/dr-dash-types';

/**
 * Snapshot mode for a Redux state slice.
 *
 * - `full`:    snapshot the entire slice value as-is
 * - `fields`:  snapshot only the listed dot-paths under this slice
 * - `exclude`: never snapshot this slice
 *
 * Slices not present in {@link STATE_SNAPSHOT_ALLOWLIST} default to `exclude`
 * at runtime — this is the safe default for privacy and byte-budget reasons.
 */
export type SliceSnapshotMode = 'full' | 'fields' | 'exclude';

type SliceSnapshotConfigBase = {
    /**
     * Optional runtime sanitizer that receives the raw slice value and returns
     * a version safe to emit. Used for slices with PII risk (see
     * `libs/accelerate/shared-store` allowlist spec, tier-1/2 audit notes).
     *
     * If provided AND the sanitizer throws, the slice is emitted as
     * `{ __sanitizerError: true }` — the middleware never lets a snapshot
     * bring down the app.
     */
    sanitize?: (value: unknown) => unknown;
};

export type SliceSnapshotConfig =
    | (SliceSnapshotConfigBase & { mode: 'full' })
    | (SliceSnapshotConfigBase & { mode: 'exclude' })
    | (SliceSnapshotConfigBase & {
          mode: 'fields';
          /**
           * The exact list of top-level field names on this slice to snapshot.
           * Nested access is not currently supported — request the whole child
           * object by name and let downstream tooling drill in.
           */
          fields: readonly string[];
      });

/**
 * Curated allowlist of dr-dash Redux state slices safe to snapshot for
 * deterministic payment-reducer replay telemetry (Rally US2039705, phase 2).
 *
 * This constant is the single source of truth for which slices are captured.
 * Slices not present here (or with `mode: 'exclude'`) are silently skipped
 * by `pickSnapshotSlice` / `pickAllSnapshotSlices` — stale entries produce
 * no runtime error, only quiet omission from telemetry output.
 *
 * Design principles:
 *   1. Snapshot the minimum state needed to deterministically replay the
 *      payment reducer given the same action.
 *   2. Include payment math inputs (offer, dealer, incentives, trade,
 *      protection, taxes) AND outputs (payment slice itself).
 *   3. Include flow-control state that alters reducer behavior (feature
 *      toggles, contracting stage, deal-lifecycle mode).
 *   4. Exclude PII (customer, credit app, DL/SSN-adjacent fields). Snapshot
 *      metadata about presence, not values.
 *   5. Exclude pure UI/modal state (activeTab, slideout visibility, print
 *      modal, spinner) — these do not affect math replay.
 *   6. Prefer whole-slice snapshots over field lists when the entire slice is
 *      math-relevant (avoids drift when new math fields are added upstream).
 *   7. Byte budget: p99 < 1 MB per session total (delta-encoded, per-field
 *      `{b, a}` shape). See Rally US2040391 AC-1.
 */
export const STATE_SNAPSHOT_ALLOWLIST: Readonly<Record<string, SliceSnapshotConfig>> = Object.freeze({
    // -----------------------------------------------------------------------
    // Tier 1 — Payment math inputs & outputs (whole-slice snapshots)
    // -----------------------------------------------------------------------
    accessories: { mode: 'full' },
    altDealStructure: { mode: 'full' },
    balloonPayments: { mode: 'full' },
    calculationDetails: { mode: 'full' },
    dealer: { mode: 'full' },
    dealerDiscounts: { mode: 'full' },
    desking: { mode: 'full' },
    deskingGlobal: { mode: 'full' },
    downPayment: { mode: 'full' },
    dueAtSigning: { mode: 'full' },
    flexBuyPayments: { mode: 'full' },
    lenderList: { mode: 'full' },
    manualFieldOverrides: { mode: 'full' },
    manualIncentives: { mode: 'full' },
    multiTradeIns: { mode: 'full' },
    offer: { mode: 'full' },
    offerInfo: { mode: 'full' },
    payment: { mode: 'full' },
    rateType: { mode: 'full' },
    selectedOffer: { mode: 'full' },
    tradeIn: { mode: 'full' },
    tradeInCard: { mode: 'full' },
    tradeIns: { mode: 'full' },
    tradeInsScoped: { mode: 'full' },
    vehicle: { mode: 'full' },
    vehicleProtection: { mode: 'full' },
    vehicleProtectionV2: { mode: 'full' },

    // Tier 1 — baseDeal (curated fields; strip UI/modal flags)
    baseDeal: {
        mode: 'fields',
        fields: [
            'aioDxgEvents',
            'breakdownActionSource',
            'calcEngineTransitionState',
            'isAutoSelectProgramChecked',
            'isVehicleInsuranceUpdated',
            'paymentCallPending',
            'showUpdateOfferForFSEvents',
            'vehicleComplianceInfo',
            'vehicleInsuranceInfo'
        ]
    },

    // -----------------------------------------------------------------------
    // Tier 2 — Flow control / diagnostics / lender-side outcomes
    // -----------------------------------------------------------------------
    baggageFuelEconomy: { mode: 'full' },
    byOfferType: { mode: 'full' },
    dealLifecycle: { mode: 'full' },
    dealMode: { mode: 'full' },
    dealScreenError: { mode: 'full' },
    dealValidation: { mode: 'full' },
    featureToggles: { mode: 'full' },
    incentiveValidation: { mode: 'full' },
    internalPrevOffer: { mode: 'full' },
    pushToDms: { mode: 'full' },
    pushToFI: { mode: 'full' },
    pushToRouteOne: { mode: 'full' },
    rollback: { mode: 'full' },
    savedInputs: { mode: 'full' },
    saveValidation: { mode: 'full' },
    tradeInComponent: { mode: 'full' },
    tradeReconCostConfig: { mode: 'full' },

    // Tier 2 — contracting (curated fields; strip noisy field validation)
    contracting: {
        mode: 'fields',
        fields: [
            'contractingStatusFailure',
            'contractingStatusInfo',
            'contractValidationLoading',
            'correlationId',
            'fsEventDelayMs',
            'isInProgress',
            'modalOpen',
            'saveOfferRequestEndTimeMs',
            'stage',
            'startTimeMs',
            'waitingForFsResponse'
        ]
    },

    // Tier 2 — creditScore (PII-adjacent: score bounds only, no raw score)
    creditScore: {
        mode: 'fields',
        fields: ['high']
    },

    // Tier 2 — pushToDarwin (may contain lender-facing identifiers; needs sanitizer)
    pushToDarwin: {
        mode: 'fields',
        fields: ['darwinResponseData'],
        sanitize: sanitizeDarwinResponse
    },

    // Tier 2 — creditDecision (whole slice, but sanitizer strips applicant PII)
    creditDecision: {
        mode: 'full',
        sanitize: sanitizeCreditDecision
    }

    // -----------------------------------------------------------------------
    // Tier 3 — Excluded (default; not listed here)
    // -----------------------------------------------------------------------
    // customer, customerSpd, customerUI, customerVehicles, creditApp,
    // editCustomerSlideout, comparison, dealSpd, printPdf, salesCheckin,
    // vAuto, docUpload, docMove — all excluded via default.
});

/**
 * Runtime sanitizer for `state.pushToDarwin.darwinResponseData`.
 *
 * Darwin response payloads can include lender-facing customer identifiers.
 * We strip anything that looks like an SSN, DL number, email, or full name;
 * fields with those names are replaced with a redaction sentinel.
 *
 * If the shape is not the expected object, returns the value as-is (the outer
 * try/catch in the middleware handles hard failures).
 */
function sanitizeDarwinResponse(value: unknown): unknown {
    return redactSensitiveFields(value, DARWIN_REDACTED_FIELDS);
}

/**
 * Runtime sanitizer for `state.creditDecision`.
 *
 * The credit decision slice may include the selected applicant's name/DOB
 * alongside the chosen lender + tier. We keep the lender selection but redact
 * anything that could identify the individual.
 */
function sanitizeCreditDecision(value: unknown): unknown {
    if (value == null || typeof value !== 'object') return value;
    return redactSensitiveFields(value, CREDIT_DECISION_REDACTED_FIELDS);
}

/** Case-insensitive field-name allowlist of things to redact. */
const DARWIN_REDACTED_FIELDS: ReadonlySet<string> = new Set([
    'ssn',
    'socialsecuritynumber',
    'driverslicense',
    'driverlicense',
    'dlnumber',
    'email',
    'emailaddress',
    'firstname',
    'lastname',
    'fullname',
    'phonenumber',
    'phone',
    'dob',
    'dateofbirth'
]);

const CREDIT_DECISION_REDACTED_FIELDS: ReadonlySet<string> = new Set([
    'firstname',
    'lastname',
    'fullname',
    'ssn',
    'socialsecuritynumber',
    'dob',
    'dateofbirth',
    'email',
    'emailaddress',
    'phonenumber',
    'phone',
    'driverslicense',
    'driverlicense',
    'dlnumber'
]);

/** Recursive redaction — replaces matching field values with `'[REDACTED]'`. */
function redactSensitiveFields(input: unknown, redactSet: ReadonlySet<string>): unknown {
    if (input === null || typeof input !== 'object') return input;
    if (Array.isArray(input)) {
        return input.map((v) => redactSensitiveFields(v, redactSet));
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
        if (redactSet.has(k.toLowerCase())) {
            out[k] = '[REDACTED]';
        } else {
            out[k] = redactSensitiveFields(v, redactSet);
        }
    }
    return out;
}

/**
 * Extract the snapshot-safe view of a single slice according to the allowlist.
 *
 * Returns `undefined` for slices excluded (or unlisted) — callers should treat
 * `undefined` as "do not emit".
 */
export function pickSnapshotSlice(state: Partial<StateTree>, sliceName: string): unknown | undefined {
    const config = STATE_SNAPSHOT_ALLOWLIST[sliceName];
    if (!config || config.mode === 'exclude') return undefined;

    const sliceValue = (state as Record<string, unknown>)[sliceName];
    if (sliceValue === undefined) return undefined;

    let picked: unknown;
    if (config.mode === 'full') {
        picked = sliceValue;
    } else {
        // mode === 'fields' — 'exclude' is short-circuited above.
        if (sliceValue == null || typeof sliceValue !== 'object') return undefined;
        const src = sliceValue as Record<string, unknown>;
        const dst: Record<string, unknown> = {};
        for (const field of config.fields) {
            if (field in src) dst[field] = src[field];
        }
        picked = dst;
    }

    if (config.sanitize) {
        try {
            return config.sanitize(picked);
        } catch {
            return { __sanitizerError: true };
        }
    }
    return picked;
}

/**
 * Build a full snapshot object containing every allowlisted slice that is
 * present in the state tree. Excluded / unlisted slices are omitted.
 *
 * The returned object mirrors the top-level state shape but restricted to the
 * allowlist — safe to serialize for the `{s: ...}` anchor payload.
 */
export function pickAllSnapshotSlices(state: Partial<StateTree>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const sliceName of Object.keys(STATE_SNAPSHOT_ALLOWLIST)) {
        const picked = pickSnapshotSlice(state, sliceName);
        if (picked !== undefined) out[sliceName] = picked;
    }
    return out;
}

/**
 * Names of all slices explicitly listed in the allowlist. Provided for the
 * middleware's "unclassified slice touched" diagnostic (see spec `gaps` note).
 */
export const ALLOWLISTED_SLICE_NAMES: ReadonlySet<string> = new Set(Object.keys(STATE_SNAPSHOT_ALLOWLIST));
